import React, { useMemo, useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Chip, Stack, Typography } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Pill, SectionCard, KpiRow } from './common'
import DataTable from './Tables'
import { versionToSummary, serviceLabel, counterCourierName } from '../apiMap'
import { useGetCouriersQuery } from '../../../redux/d2cApiSlice'
import { useGetAllCourierPartnersQuery } from '../../../redux/commonApiCalls'

const MODE_LABEL = { margin: 'Target Margin', ratecard: 'Target Rate Card' }
const RATE_ZONES = ['Local', 'Within State', 'Metros', 'ROI', 'Special']

/**
 * ProspectAndCommercialSummary — everything the Sales Leader filled in Steps
 * 1–3, read-only, for wherever a proposal's details are already shown
 * (ProposalDetailModal's "View" modal, DealReview's "Review" page). Built
 * entirely from what the backend persists (via apiMap's versionToSummary) —
 * no fabricated values.
 *
 * showRfi: set false where the RFI/pickup split is already rendered by the
 * caller (DealReview) to avoid duplicating that section.
 */
// bare: return just the content — KPI tiles + the per-service commercial blocks, no
// SectionCards/accordions of its own — for callers that wrap it in ONE section of their
// own (DealReview's "② The ask" card in the command-header layout).
export default function ProspectAndCommercialSummary({ version, proposalRaw = {}, showRfi = true, showCommercial = true, expandCommercial = false, bare = false }) {
  // Same courier master + fallback partner list ServiceBlock (NewProposal.js)
  // uses to resolve cpAccountCode → a readable "Parent · code" label.
  const { data: d2cCouriers = [] } = useGetCouriersQuery()
  const { data: fallbackCouriers = [] } = useGetAllCourierPartnersQuery({ offset: 0, limit: 15000, excludes: 'sla' })
  const couriers = d2cCouriers.length ? d2cCouriers : fallbackCouriers
  const parentOf = (c) => c.parent || c.name || c.cpAccountCode || c.code
  const byCode = useMemo(() => new Map(couriers.map((c) => [c.cpAccountCode || c.code, c])), [couriers])
  const courierLabel = (code) => { const c = byCode.get(code); return c ? `${parentOf(c)} · ${code}` : code }

  const summary = useMemo(() => versionToSummary(version || {}, proposalRaw || {}), [version, proposalRaw])
  if (!version) return null

  const rfiCols = [
    { key: 'service', label: 'Service', render: (r) => <b>{r.service}</b> },
    { key: 'zone', label: 'Zone' },
    { key: 'slab', label: 'Weight slab' },
    { key: 'pct', label: 'Volume split', align: 'right' },
  ]
  const pickupCols = [
    { key: 'pin', label: 'Pickup PIN', render: (r) => <b>{r.pin}</b> },
    { key: 'zone', label: 'Zone' },
    { key: 'vol', label: 'Monthly volume', align: 'right' },
  ]
  const rateCols = [
    { key: 'slab', label: 'Weight slab' },
    ...RATE_ZONES.map((z) => ({ key: z, label: z, align: 'right' })),
  ]

  // bare mode adds a 4th tile (avg invoice + COD/RTO share) — the command-header layout
  // shows the deal's money profile as one KPI row instead of scattered pills.
  const fwd = summary.services.find((s) => s.serviceType !== 'RVP')
  const kpiItems = [
    { label: 'Total monthly volume', value: summary.totalMonthlyVolume != null ? summary.totalMonthlyVolume.toLocaleString('en-IN') : '—', delta: 'shipments / mo' },
    { label: 'Revenue potential', value: summary.monthlyRevenuePotentialLacs != null ? `₹${summary.monthlyRevenuePotentialLacs}L` : '—', delta: 'per month' },
    { label: 'Pickup locations', value: String(summary.pickupLocations.length || 0), delta: summary.pickupLocations.length === 1 ? 'warehouse' : 'warehouses' },
    ...(bare && fwd?.cod.avgInvoiceValueInr != null ? [{
      label: 'Avg invoice',
      value: `₹${fwd.cod.avgInvoiceValueInr}`,
      delta: [fwd.cod.codShipmentPct != null ? `COD ${fwd.cod.codShipmentPct}%` : null, fwd.cod.rtoShipmentPct != null ? `RTO ${fwd.cod.rtoShipmentPct}%` : null].filter(Boolean).join(' · ') || 'per order',
    }] : []),
  ]

  const commercialBody = (
    <>
      {!summary.services.length && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No services on this version.</Typography>}
      <Stack spacing={2}>
        {summary.services.map((s) => (
          <Box key={s.serviceType} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{s.label}</Typography>
              <Pill label={MODE_LABEL[s.mode] || s.mode} styleKey="purple" />
              <Pill label={`SOW ${s.sowPct ?? '—'}%`} styleKey="neutral" />
              <Pill label={s.marginPct == null ? 'Margin masked' : `Margin ${s.marginPct}%`} styleKey="neutral" />
              {s.mode === 'margin' && s.targetMarginPct != null && <Pill label={`Target ${s.targetMarginPct}%`} styleKey="neutral" />}
            </Stack>

            <Box sx={{ mb: 1 }}>
              <Field label="Cities in scope" value={s.citiesInScope.length ? s.citiesInScope.join(', ') : '—'} wide />
            </Box>

            <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap', gap: 2, mb: 1 }}>
              <Box sx={{ minWidth: 220, flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Courier preference</Typography>
                {s.cpPreference.length
                  ? (
                    <Stack spacing={0.25} sx={{ mt: 0.5 }}>
                      {s.cpPreference.map((code, i) => <Typography key={code} sx={{ fontSize: 12 }}>{i + 1}. {courierLabel(code)}</Typography>)}
                    </Stack>
                  )
                  : <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>None specified — Prozo’s allocation logic applies.</Typography>}
              </Box>
              <Box sx={{ minWidth: 220, flex: 1 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>Blacklisted couriers</Typography>
                {s.courierBlacklist.length
                  ? (
                    <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {s.courierBlacklist.map((code) => <Chip key={code} size="small" label={courierLabel(code)} />)}
                    </Stack>
                  )
                  : <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>None.</Typography>}
              </Box>
            </Stack>

            {/* Only fields the SL actually entered render — as compact Pills, the same
                pattern already used for "Evidence shared" right below. A grid of 9 labels
                where 6 just say "—" is noise; a reviewer cares about what WAS set (a
                missing required field would already have blocked Step 3's own validation
                before this deal ever reached review). */}
            {(() => {
              const codPills = s.serviceType !== 'RVP' ? [
                ['COD fee — flat', s.cod.flat != null ? `₹${s.cod.flat}` : null],
                ['COD fee — % of value', s.cod.pct != null ? `${s.cod.pct}%` : null],
                ['RTO — % of forward', s.cod.rtoPct != null ? `${s.cod.rtoPct}%` : null],
                ['COD share of shipments', s.cod.codShipmentPct != null ? `${s.cod.codShipmentPct}%` : null],
                ['RTO share of shipments', s.cod.rtoShipmentPct != null ? `${s.cod.rtoShipmentPct}%` : null],
                ['Prepaid RTO % of shipments', s.cod.prepaidRtoShipmentPct != null ? `${s.cod.prepaidRtoShipmentPct}%` : null],
                ['COD RTO % of shipments', s.cod.codRtoShipmentPct != null ? `${s.cod.codRtoShipmentPct}%` : null],
                ['COD share of RTO (legacy)', s.cod.rtoCodShipmentPct != null ? `${s.cod.rtoCodShipmentPct}%` : null],
                ['Avg invoice value', s.cod.avgInvoiceValueInr != null ? `₹${s.cod.avgInvoiceValueInr}` : null],
              ] : [
                ['Min RVP cost', s.rvp.minCost != null ? `₹${s.rvp.minCost}` : null],
                ['RVP cost — % of forward', s.rvp.pctOfForward != null ? `${s.rvp.pctOfForward}%` : null],
                ['QC charge', s.rvp.qc != null ? `₹${s.rvp.qc}` : null],
              ]
              const set = codPills.filter(([, v]) => v != null)
              return set.length ? (
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75, mb: 1.5 }}>
                  {set.map(([label, value]) => <Pill key={label} label={`${label}: ${value}`} styleKey="neutral" />)}
                </Stack>
              ) : (
                <Typography sx={{ fontSize: 12, color: 'text.secondary', mb: 1.5 }}>
                  {s.serviceType !== 'RVP' ? 'No COD/RTO commercials entered.' : 'No RVP commercials entered.'}
                </Typography>
              )
            })()}

            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Pill label={s.evidenceShared ? 'Evidence shared: Yes' : 'Evidence shared: No'} styleKey={s.evidenceShared ? 'approved' : 'neutral'} />
              {!!s.goAheads.length && <Pill label={`${s.goAheads.length} unserviceable-city go-ahead(s)`} styleKey="returned" />}
            </Stack>

            {s.mode === 'ratecard' && !!s.targetRateCards.length && (
              <Box sx={{ mt: 1.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', mb: 0.5 }}>Target rate card</Typography>
                {s.targetRateCards.map((card, ci) => (
                  <Box key={ci} sx={{ mb: 1 }}>
                    {card.courierId && <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{counterCourierName(version, card.courierId)}</Typography>}
                    <DataTable
                      columns={rateCols}
                      rows={(card.rows || []).map((r, i) => ({ _k: i, slab: r.weightSlab, ...r.zoneRates }))}
                      getRowKey={(r) => r._k}
                      emptyText="No rate-card rows."
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ))}
      </Stack>
    </>
  )

  // bare: the command-header layout's own compact language — quiet bordered KPI tiles
  // (not the big coloured KpiRow cards) and a tight pill-based service block, per the
  // approved "Direction 2" mockup. The caller provides the section chrome.
  if (bare) {
    const commercePills = (s) => (s.serviceType !== 'RVP' ? [
      ['COD fee (flat)', s.cod.flat != null ? `₹${s.cod.flat}` : null],
      ['COD fee (% of value)', s.cod.pct != null ? `${s.cod.pct}%` : null],
      ['RTO (% of forward)', s.cod.rtoPct != null ? `${s.cod.rtoPct}%` : null],
      ['COD share of shipments', s.cod.codShipmentPct != null ? `${s.cod.codShipmentPct}%` : null],
      ['RTO share of shipments', s.cod.rtoShipmentPct != null ? `${s.cod.rtoShipmentPct}%` : null],
      ['Prepaid RTO % of shipments', s.cod.prepaidRtoShipmentPct != null ? `${s.cod.prepaidRtoShipmentPct}%` : null],
      ['COD RTO % of shipments', s.cod.codRtoShipmentPct != null ? `${s.cod.codRtoShipmentPct}%` : null],
      ['Avg invoice value', s.cod.avgInvoiceValueInr != null ? `₹${s.cod.avgInvoiceValueInr}` : null],
    ] : [
      ['Min RVP cost', s.rvp.minCost != null ? `₹${s.rvp.minCost}` : null],
      ['RVP cost (% of forward)', s.rvp.pctOfForward != null ? `${s.rvp.pctOfForward}%` : null],
      ['QC charge', s.rvp.qc != null ? `₹${s.rvp.qc}` : null],
    ]).filter(([, v]) => v != null)
    return (
      <>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 1.25, mb: 2 }}>
          {kpiItems.map((k) => (
            <Box key={k.label} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', px: 1.75, py: 1.25, bgcolor: '#FAFBFD' }}>
              <Typography sx={{ fontSize: 10.5, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary' }}>{k.label}</Typography>
              <Typography sx={{ fontSize: 19, fontWeight: 800, mt: 0.25, lineHeight: 1.2 }}>{k.value}</Typography>
              <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>{k.delta}</Typography>
            </Box>
          ))}
        </Box>
        {showCommercial && !summary.services.length && <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>No services on this version.</Typography>}
        {showCommercial && (
          <Stack spacing={1.5}>
            {summary.services.map((s) => (
              <Box key={s.serviceType} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '12px', p: 2, bgcolor: '#FAFBFD' }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 1, mb: 1.25 }}>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>{s.label}</Typography>
                  <Pill label={MODE_LABEL[s.mode] || s.mode} styleKey="purple" />
                  <Pill label={`SOW ${s.sowPct ?? '—'}%`} styleKey="neutral" />
                  <Pill label={s.marginPct == null ? 'Margin masked' : `Margin ${s.marginPct}%`} styleKey="neutral" />
                  {s.mode === 'margin' && s.targetMarginPct != null && <Pill label={`Target ${s.targetMarginPct}%`} styleKey="neutral" />}
                </Stack>
                {/* Labeled info grid — every value under its own small caption, so couriers,
                    blacklist, cities and the COD/RTO numbers never blur into one pill soup. */}
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', rowGap: 1.5, columnGap: 2.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 0.5 }}>Courier preference (priority order)</Typography>
                    {s.cpPreference.length ? (
                      /* One numbered chip per courier — boundaries are physical, so a long
                         "Parent · account code" label can never bleed into its neighbour. */
                      <Stack direction="row" spacing={0.75} sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                        {s.cpPreference.map((code, i) => (
                          <Box key={code} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, border: '1px solid', borderColor: 'divider', borderRadius: '999px', bgcolor: 'background.paper', pl: 0.5, pr: 1.5, py: 0.4 }}>
                            <Box sx={{ width: 18, height: 18, borderRadius: '50%', bgcolor: '#EAF1FC', color: '#103A75', fontSize: 10.5, fontWeight: 800, lineHeight: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{i + 1}</Box>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, lineHeight: 1, whiteSpace: 'nowrap' }}>{courierLabel(code)}</Typography>
                          </Box>
                        ))}
                      </Stack>
                    ) : (
                      <Typography sx={{ fontSize: 12.5, fontWeight: 600 }}>None — Prozo default allocation</Typography>
                    )}
                  </Box>
                  <Info label="Blacklisted couriers" value={s.courierBlacklist.length ? s.courierBlacklist.map(courierLabel).join(', ') : 'None'} danger={!!s.courierBlacklist.length} />
                  {!!s.citiesInScope.length && <Info label="Cities in scope" value={s.citiesInScope.join(', ')} />}
                  {commercePills(s).map(([label, value]) => <Info key={label} label={label} value={value} />)}
                  <Info label="Evidence shared" value={s.evidenceShared ? 'Yes' : 'No'} />
                </Box>
                {!!s.goAheads.length && (
                  <Box sx={{ mt: 1.25 }}>
                    <Pill label={`${s.goAheads.length} unserviceable-city go-ahead(s)`} styleKey="returned" />
                  </Box>
                )}
                {s.mode === 'ratecard' && !!s.targetRateCards.length && (
                  <TargetRateCardCollapse cards={s.targetRateCards} rateCols={rateCols} version={version} />
                )}
              </Box>
            ))}
          </Stack>
        )}
      </>
    )
  }

  return (
    <>
      <SectionCard title="① Prospect details" subtitle="Everything the Sales Leader entered in Step 1, as stored on this deal.">
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
          <Pill label={summary.brand || '—'} styleKey="purple" />
          {summary.hubspot && <Pill label={`HubSpot: ${summary.hubspot}`} styleKey="neutral" />}
          {summary.card && <Pill label={summary.card} styleKey="neutral" />}
        </Stack>
        <KpiRow items={kpiItems} />
      </SectionCard>

      {showRfi && (
        <Accordion elevation={0} sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: '10px', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.25 }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'primary.dark' }}>② Volume split (RFI)</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>How the Sales Leader said shipments spread across zones and weight bands.</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.25, pt: 0 }}>
            {!!summary.pickupLocations.length && (
              <Box sx={{ mb: 1.5 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Pickup locations</Typography>
                <DataTable
                  columns={pickupCols}
                  rows={summary.pickupLocations.map((p, i) => ({ _k: i, pin: p.pincode, zone: p.zoneTag, vol: (p.monthlyVolume ?? 0).toLocaleString('en-IN') }))}
                  getRowKey={(r) => r._k}
                  emptyText="—"
                />
              </Box>
            )}
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', mb: 0.5 }}>Volume split — service × zone × weight slab</Typography>
            <DataTable
              columns={rfiCols}
              rows={summary.rfiEntries.map((e, i) => ({ _k: i, service: serviceLabel(e.serviceType), zone: e.zone, slab: e.weightSlab, pct: e.splitPct != null ? `${e.splitPct}%` : '—' }))}
              getRowKey={(r) => r._k}
              emptyText="No RFI split captured on this version."
            />
          </AccordionDetails>
        </Accordion>
      )}

      {/* DealReview (the actual review/approval screen) wants this always visible —
          a reviewer shouldn't have to click to see the numbers they're approving.
          Other callers (SLCounterReview, ProposalDetailModal's "View") keep the
          original collapsed accordion. */}
      {showCommercial && (expandCommercial ? (
        <SectionCard title="③ Commercial expectations — by service type" subtitle="What the Sales Leader asked for, per service, in Step 3.">
          {commercialBody}
        </SectionCard>
      ) : (
        <Accordion elevation={0} sx={{ mb: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)', borderRadius: '10px', '&:before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.25 }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: 'primary.dark' }}>③ Commercial expectations — by service type</Typography>
              <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25, lineHeight: 1.5 }}>What the Sales Leader asked for, per service, in Step 3.</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2.25, pt: 0 }}>{commercialBody}</AccordionDetails>
        </Accordion>
      ))}
    </>
  )
}

// TargetRateCardCollapse — the client's full target rate card (review surfaces),
// collapsed by default behind the same dashed toggle the RFI matrices use: at 10+
// slabs the table dominates the section, and reviewers mostly work off the margin
// heatmap — the typed card is reference material.
function TargetRateCardCollapse({ cards, rateCols, version }) {
  const [open, setOpen] = useState(false)
  const total = (cards[0]?.rows || []).length
  return (
    <Box sx={{ mt: 1.5 }}>
      <Button fullWidth size="small" onClick={() => setOpen((v) => !v)} sx={{
        textTransform: 'none', fontSize: 12, fontWeight: 700, borderRadius: '8px', py: 0.6,
        color: 'primary.main', border: '1.5px dashed', borderColor: '#CFE0F7', bgcolor: '#F3F7FE',
        '&:hover': { bgcolor: '#EAF1FC', borderColor: 'primary.main' },
      }}>
        {open ? 'Hide target rate card ▴' : `Show target rate card (${total} slab${total === 1 ? '' : 's'}) ▾`}
      </Button>
      {open && (
        <Box sx={{ mt: 1 }}>
          {cards.map((card, ci) => (
            <Box key={ci} sx={{ mb: 1 }}>
              {card.courierId && <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{counterCourierName(version, card.courierId)}</Typography>}
              <DataTable
                columns={rateCols}
                rows={(card.rows || []).map((r, i) => ({ _k: i, slab: r.weightSlab, ...r.zoneRates }))}
                getRowKey={(r) => r._k}
                emptyText="No rate-card rows."
              />
            </Box>
          ))}
        </Box>
      )}
    </Box>
  )
}

// Info — one labelled value in the bare-mode service grid: small uppercase caption
// over a readable 12.5px value (red when it flags something, e.g. a blacklist).
function Info({ label, value, danger = false }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'text.secondary', mb: 0.25 }}>{label}</Typography>
      <Typography sx={{ fontSize: 12.5, fontWeight: 600, lineHeight: 1.5, overflowWrap: 'anywhere', color: danger ? '#B42318' : 'text.primary' }}>{value}</Typography>
    </Box>
  )
}

function Field({ label, value, wide }) {
  return (
    <Box sx={{ minWidth: wide ? 260 : 140 }}>
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase' }}>{label}</Typography>
      <Typography sx={{ fontSize: 12 }}>{value}</Typography>
    </Box>
  )
}
